import { z } from "zod";

import { RateLimitSettingsSchema } from "../RateLimit.schema";

/**
 * Embed Security Settings Schema
 *
 * EMBED-ONLY security settings that combine:
 * - Domain/visibility restrictions (EMBED-specific - not needed for Office Add-ins)
 * - Rate limiting (shared via RateLimitSettingsSchema)
 *
 * Why merge?
 * - EMBED needs both domain controls AND rate limiting
 * - Office Add-ins only need rate limiting (they use API key auth, not domain-based)
 * - DRY: Rate limit validation rules defined once in RateLimitSettingsSchema
 */
export const EmbedSecuritySettingsSchema = z
  .object({
    // EMBED-only: Domain/visibility controls
    visibility: z.enum(["public", "private"]),
    allowOnlyOnSpecificDomains: z.boolean(),
    allowedDomains: z.array(z.string()),
    /**
     * Let a BROWSER holding no credential at all mint a chat-session token for
     * this deployment, at
     * `POST /public/v1/deployments/:deploymentId/chat-session/anonymous`.
     *
     * 🔴 OPTIONAL, AND ABSENT MEANS OFF. That is the whole safety property of
     * this field. Every deployment stored before it existed parses without it,
     * and an org that turned an anonymous door on for one embed must not have
     * acquired one on every other embed it owns. A required boolean would have
     * failed the parse on every legacy row instead, and the fallback that
     * repairs such a parse is exactly where a silent default-on gets written.
     *
     * It is deliberately SEPARATE from `visibility`. `visibility: "public"` is
     * the DEFAULT for a new embed and means "the widget we serve may be shown";
     * reusing it here would have handed an anonymous credential-minting door to
     * every embed deployment in the estate on the day this shipped. This field
     * says something narrower and newer: a page we do not serve, on an origin
     * we do not control, may ask us for a token. Both must be true — the mint
     * refuses a private widget as well as an un-opted-in one.
     *
     * The org-API-key mint at `.../chat-session` does NOT read this. That path
     * is authorized by a key the customer's own server holds, so it needs no
     * per-deployment opt-in and never had one.
     */
    allowAnonymousChatSessions: z.boolean().optional()
  })
  .merge(RateLimitSettingsSchema); // ← Merge shared rate limit fields

export type EmbedSecuritySettings = z.infer<typeof EmbedSecuritySettingsSchema>;
